import { useSyncExternalStore } from 'react';
import { analyticsEnabled, getConsent, setConsent, subscribeConsent } from '../utils/analytics';
import { Language } from '../types';
import { t } from '../utils/i18n';

export function useConsent() {
  return useSyncExternalStore(subscribeConsent, getConsent, () => 'unset' as const);
}

/** Kleine, niet-blokkerende vraag om anonieme statistieken. Verschijnt alleen als analytics aanstaat. */
export function AnalyticsConsent({ language }: { language: Language }) {
  const consent = useConsent();
  if (!analyticsEnabled() || consent !== 'unset') return null;
  return (
    <div
      role="region"
      aria-label={t(language, 'analyticsTitle')}
      className="fixed inset-x-0 bottom-3 z-[110] flex justify-center px-4 pointer-events-none"
    >
      <div className="pointer-events-auto w-full max-w-xl space-y-3 rounded-2xl bg-slate-900/95 p-4 text-sm text-slate-50 backdrop-blur-md dark:bg-slate-100/95 dark:text-slate-900">
        <p>{t(language, 'analyticsAsk')}</p>
        <div className="flex gap-2">
          <button onClick={() => setConsent('granted')} className="min-h-11 whitespace-nowrap rounded-2xl bg-slate-50 px-4 font-semibold text-slate-900 transition-all duration-150 active:scale-95 dark:bg-slate-900 dark:text-slate-50 cursor-pointer">
            {t(language, 'analyticsAllow')}
          </button>
          <button onClick={() => setConsent('denied')} className="min-h-11 whitespace-nowrap rounded-2xl px-4 font-semibold transition-all duration-150 active:scale-95 cursor-pointer">
            {t(language, 'analyticsDeny')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Aan/uit in de instellingen, zodat een keuze altijd te wijzigen is. */
export function AnalyticsToggle({ language }: { language: Language }) {
  const consent = useConsent();
  if (!analyticsEnabled()) return null;
  const on = consent === 'granted';
  return (
    <div className="space-y-2.5">
      <h3 className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">{t(language, 'analyticsTitle')}</h3>
      <p className="text-sm text-slate-600 dark:text-slate-400">{t(language, 'analyticsDesc')}</p>
      <button
        onClick={() => setConsent(on ? 'denied' : 'granted')}
        aria-pressed={on}
        className={`min-h-11 whitespace-nowrap rounded-2xl px-4 text-sm font-bold transition-all duration-150 active:scale-95 cursor-pointer ${
          on ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-black/5 text-slate-800 dark:bg-white/10 dark:text-slate-200'
        }`}
      >
        {on ? t(language, 'analyticsOn') : t(language, 'analyticsOff')}
      </button>
    </div>
  );
}
